/**
 * WebRTC Meeting Component
 * Main component for WebRTC video calls (replaces Jitsi)
 */

import React, { useEffect, useState } from 'react';
import { Spin, Alert, Space } from 'antd';
import { useWebRTC } from '@/hooks/useWebRTC';
import { VideoGrid } from './VideoGrid';
import { VideoControls } from './VideoControls';
import { ConnectionStatus } from './ConnectionStatus';
import type { WebRTCMeetingProps } from '@/api/types/webrtc.types';
import './WebRTCMeeting.css';

export const WebRTCMeeting: React.FC<WebRTCMeetingProps> = ({
  roomName,
  userId,
  displayName,
  tenantId,
  onLeave,
  onReady,
  onError,
}) => {
  const [isJoining, setIsJoining] = useState(true);

  // Main WebRTC hook
  const {
    localStream,
    peers,
    isConnected,
    isJoined,
    connectionState,
    error,
    joinRoom,
    leaveRoom,
    toggleAudio,
    toggleVideo,
    startScreenShare,
    stopScreenShare,
    isAudioEnabled,
    isVideoEnabled,
    isScreenSharing,
  } = useWebRTC({
    roomName: `${tenantId}-meeting-${roomName}`, // Format: {tenant_id}-meeting-{meetingId}
    userId,
    displayName,
    tenantId,
    autoJoin: false, // Manual join after loading
  });

  // Join room when connected
  useEffect(() => {
    if (isConnected && !isJoined && localStream) {
      console.log('🚪 Auto-joining room after connection established');
      joinRoom();
      setIsJoining(false);
      onReady?.();
    }
  }, [isConnected, isJoined, localStream, joinRoom, onReady]);

  // Handle errors
  useEffect(() => {
    if (error) {
      console.error('❌ WebRTC error:', error);
      onError?.(error);
    }
  }, [error, onError]);

  // Handle leave
  const handleLeave = () => {
    leaveRoom();
    onLeave?.();
  };

  // Handle screen share toggle
  const handleToggleScreenShare = async () => {
    if (isScreenSharing) {
      stopScreenShare();
    } else {
      try {
        await startScreenShare();
      } catch (err) {
        console.error('❌ Failed to start screen share:', err);
      }
    }
  };

  // Loading state
  if (isJoining && !error) {
    return (
      <div className="webrtc-meeting-loading">
        <Space direction="vertical" align="center" size="large">
          <Spin size="large" />
          <div className="loading-text">
            {!localStream && 'Requesting camera and microphone access...'}
            {localStream && !isConnected && 'Connecting to server...'}
            {localStream && isConnected && 'Joining meeting...'}
          </div>
        </Space>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="webrtc-meeting-error">
        <Alert
          message="Failed to join video call"
          description={error.message}
          type="error"
          showIcon
          action={
            <Space>
              <button onClick={() => window.location.reload()}>Retry</button>
              <button onClick={handleLeave}>Leave</button>
            </Space>
          }
        />
      </div>
    );
  }

  return (
    <div className="webrtc-meeting">
      {/* Connection status header */}
      <div className="webrtc-meeting-header">
        <ConnectionStatus state={connectionState} peersCount={peers.size} />
      </div>

      {/* Video grid */}
      <div className="webrtc-meeting-content">
        <VideoGrid
          localStream={localStream}
          peers={peers}
          localDisplayName={displayName}
          isAudioEnabled={isAudioEnabled}
          isVideoEnabled={isVideoEnabled}
        />
      </div>

      {/* Controls footer */}
      <div className="webrtc-meeting-controls">
        <VideoControls
          isAudioEnabled={isAudioEnabled}
          isVideoEnabled={isVideoEnabled}
          isScreenSharing={isScreenSharing}
          onToggleAudio={toggleAudio}
          onToggleVideo={toggleVideo}
          onToggleScreenShare={handleToggleScreenShare}
          onLeave={handleLeave}
          disabled={!isJoined}
        />
      </div>
    </div>
  );
};
